import {
  ICareProposal,
  IDeliveryAddress,
  IIntegratedCare,
  INursingHome,
  INursingService,
  IPatient,
  IPharmacy,
  IPostalCode,
} from '@alberta/konexi-shared';
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DeliveryAddressViewModel } from 'src/app/business/order/delivery-address-viewmodel';
import { map } from 'src/app/common/mapper/mapper';
import {
  CareProposalDB,
  IntegratedCareDB,
  NursingHomeDB,
  NursingServiceDB,
  PharmacyDB,
  PostalCodeDB,
  UsersDB,
} from 'src/app/common/repository/databases';
import { UserDto } from 'src/app/shared/models/user-management/user-dto.model';
import { AuthService } from 'src/app/shared/services/auth.service';

import { SelectionItem } from '../models/selectionItem';
import { IQueryService } from './contracts/query/query-service';
import { QueryService } from './query/query.service';
import { IRoleTranslation } from './tenant-config/caregiver-role-translation';
import { TenantConfigService } from './tenant-config/tenant-config.service';

@Injectable({
  providedIn: 'root',
})
export class DeliveryAddressesService {
  constructor(
    @Inject(QueryService) private _queryService: IQueryService,
    private translateService: TranslateService,
    private _authService: AuthService,
    private _tenantConfigService: TenantConfigService
  ) {}
  private deliveryAddresses: DeliveryAddressViewModel[] = [];

  public async getDeliveryAddressesfromPatient(
    patient: IPatient,
    loadDeliveryAdress?: boolean,
    careProposalId?: string,
    isIntegratedCare?: boolean
  ) {
    this.deliveryAddresses = [];
    /* Order is of the Addresses is important! Don't change! */
    if (loadDeliveryAdress) {
      this.checkIfPatientHasDeliveryAdress(patient);
    }
    await this.initializePatientAddress(patient);
    await this.initializeNursingHomeAddress(patient);
    await this.initializeNursingServiceAddress(patient);
    await this.initializeCareGiverAddresses(patient);
    if (careProposalId) {
      await this.initializePharmacyAddress(careProposalId, isIntegratedCare);
    }
    return this.deliveryAddresses;
  }

  public checkDeliveryAddressChanged(
    deliveryAddress: IDeliveryAddress,
    predefinedAddresses: IDeliveryAddress[]
  ): boolean {
    if (!deliveryAddress || !predefinedAddresses) {
      return false;
    }
    const existingDeliveryAddress = predefinedAddresses.find(
      da =>
        da.address === deliveryAddress.address &&
        da.additionalAddress === deliveryAddress.additionalAddress &&
        da.additionalAddress2 === deliveryAddress.additionalAddress2 &&
        da.postalCode === deliveryAddress.postalCode &&
        da.city === deliveryAddress.city
    );
    return existingDeliveryAddress === undefined;
  }

  public createDeliveryAddressesSelectionItems(deliveryAddresses: DeliveryAddressViewModel[]): SelectionItem[] {
    const addressSelectionItems: SelectionItem[] = [];
    deliveryAddresses.forEach(item => {
      let additionalAddress = item.additionalAddress ? `${item.additionalAddress}\n` : '';
      additionalAddress += item.additionalAddress2 ? `${item.additionalAddress2}\n` : '';
      addressSelectionItems.push(
        new SelectionItem(
          item,
          `${item.type}: ${item.name} \n${additionalAddress} \n${item.address} \n${item.postalCode} ${item.city}`
        )
      );
    });
    return addressSelectionItems;
  }

  public async getUserAddress(): Promise<DeliveryAddressViewModel> {
    const userId = await this._authService.getCurrentUserId();
    const currentUser = await this._queryService.get<UserDto>(userId, UsersDB);
    if (currentUser && currentUser.address && currentUser.city && currentUser.postalCode && currentUser.postalCodeId) {
      const userAddress = new DeliveryAddressViewModel();
      userAddress.address = currentUser.address;
      userAddress.city = currentUser.city;
      userAddress.postalCode = currentUser.postalCode;
      userAddress.postalCodeId = currentUser.postalCodeId;
      userAddress.type = 'Außendienst';
      userAddress.name = this.getCombinedName(currentUser.firstName, currentUser.lastName);
      return userAddress;
    }
  }

  private checkIfPatientHasDeliveryAdress(patient: IPatient) {
    if (patient.deliveryAddress) {
      const deliveryAddress = map(patient.deliveryAddress, new DeliveryAddressViewModel());
      deliveryAddress.type = 'abw. Lieferadresse';
      this.deliveryAddresses.push(deliveryAddress);
    }
  }
  private initializePatientAddress(patient: IPatient) {
    if (patient.address && patient.postalCodeId) {
      const patientAddress = new DeliveryAddressViewModel();
      patientAddress.gender = patient.gender;
      patientAddress.title = patient.title;
      patientAddress.name = this.getCombinedName(patient.firstName, patient.lastName);
      patientAddress.address = patient.address;
      patientAddress.additionalAddress = patient.additionalAddress;
      patientAddress.additionalAddress2 = patient.additionalAddress2;
      patientAddress.postalCode = patient.postalCode;
      patientAddress.city = patient.city;
      patientAddress.type = 'Patient';
      patientAddress.postalCodeId = patient.postalCodeId;
      this.deliveryAddresses.push(patientAddress);
    }
  }

  private async initializeCareGiverAddresses(patient: IPatient) {
    if (patient.careGivers) {
      for (const careGiver of patient.careGivers) {
        if (careGiver.address && careGiver.postalCodeId) {
          const careGiverAddress = new DeliveryAddressViewModel();
          careGiverAddress.gender = careGiver.gender;
          careGiverAddress.title = careGiver.title;
          careGiverAddress.name = this.getCombinedName(careGiver.firstName, careGiver.lastName);
          careGiverAddress.address = careGiver.address;

          let roleTranslation: string;
          if (careGiver.role) {
            const roleTranslations: IRoleTranslation = await this._tenantConfigService.getRoleTranslation();

            roleTranslation = roleTranslations?.[careGiver.role]?.[this.translateService.currentLang];
          }
          careGiverAddress.type = roleTranslation || 'Kontakt';

          careGiverAddress.postalCodeId = careGiver.postalCodeId;
          await this.getPostalCodeCity(careGiverAddress, careGiver.postalCodeId);
          this.deliveryAddresses.push(careGiverAddress);
        }
      }
    }
  }

  private async initializePharmacyAddress(careProposalId: string, isIntegratedCare: boolean) {
    let pharmacyId = null;
    if (isIntegratedCare) {
      const integratedCare = await this._queryService.get<IIntegratedCare>(careProposalId, IntegratedCareDB);
      pharmacyId = integratedCare.pharmacyId;
    } else {
      const careProposal = await this._queryService.get<ICareProposal>(careProposalId, CareProposalDB);
      if (careProposal != null) {
        pharmacyId = careProposal.pharmacyId;
      }
    }

    if (pharmacyId == null) {
      return;
    }

    const pharmacy = await this._queryService.get<IPharmacy>(pharmacyId, PharmacyDB);
    if (pharmacy == null) {
      return;
    }
    const pharmacyAddress = new DeliveryAddressViewModel();
    pharmacyAddress.name = pharmacy.name;
    pharmacyAddress.address = pharmacy.address;
    pharmacyAddress.city = pharmacy.city;
    pharmacyAddress.postalCode = pharmacy.postalCode;
    pharmacyAddress.type = 'Apotheke';
    this.deliveryAddresses.push(pharmacyAddress);
  }

  private async initializeNursingServiceAddress(patient: IPatient) {
    if (patient.nursingServiceId) {
      const nursingService = await this._queryService.get<INursingService>(patient.nursingServiceId, NursingServiceDB);
      if (nursingService == null) {
        return;
      }
      const nursingServiceAddress = new DeliveryAddressViewModel();
      nursingServiceAddress.name = nursingService.name;
      nursingServiceAddress.address = nursingService.address;
      nursingServiceAddress.additionalAddress = `${patient.firstName} ${patient.lastName}`;
      nursingServiceAddress.city = nursingService.city;
      nursingServiceAddress.postalCode = nursingService.postalCode;
      nursingServiceAddress.type = 'Pflegedienst';
      this.deliveryAddresses.push(nursingServiceAddress);
    }
  }

  private async initializeNursingHomeAddress(patient: IPatient) {
    if (patient.nursingHomeId) {
      const nursingHome = await this._queryService.get<INursingHome>(patient.nursingHomeId, NursingHomeDB);
      if (nursingHome == null) {
        return;
      }

      const nursingHomeAddress = new DeliveryAddressViewModel();
      nursingHomeAddress.name = this.getCombinedName(patient.firstName, patient.lastName);
      nursingHomeAddress.address = nursingHome.address;
      nursingHomeAddress.additionalAddress = nursingHome.name;
      nursingHomeAddress.additionalAddress2 = patient.additionalAddress2;
      nursingHomeAddress.type = 'Pflegeheim';
      nursingHomeAddress.city = nursingHome.city;
      nursingHomeAddress.postalCode = nursingHome.postalCode;
      this.deliveryAddresses.push(nursingHomeAddress);
    }
  }

  private async getPostalCodeCity(
    deliveryAddressViewModel: DeliveryAddressViewModel,
    postalCodeId: string
  ): Promise<void> {
    const postalCodeItem = await this._queryService.get<IPostalCode>(postalCodeId, PostalCodeDB);
    if (postalCodeItem) {
      deliveryAddressViewModel.postalCode = postalCodeItem.postalCode;
      deliveryAddressViewModel.city = postalCodeItem.city;
    }
  }

  private getCombinedName(firstName: string, lastName: string): string {
    return [firstName, lastName].filter(item => !!item).join(' ');
  }

  public setDeliveryDate(): Date {
    const nextDeliveryDate = new Date();
    if (nextDeliveryDate.getDay() === 5 || nextDeliveryDate.getDay() === 6) {
      nextDeliveryDate.setDate(nextDeliveryDate.getDate() + 3);
    } else {
      nextDeliveryDate.setDate(nextDeliveryDate.getDate() + 2);
    }
    return nextDeliveryDate;
  }
}
